// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { isValidPhoneNumber } from 'libphonenumber-js';
import { parseDate } from '@shared/ui-kit/utils/format-date';
import dayjs, { type Dayjs } from 'dayjs';
import { z } from 'zod';
import {
  INVERSIS_COUNTRY_OPTIONS,
  INVERSIS_DOCUMENT_TYPE_OPTIONS,
  INVERSIS_PROVINCE_OPTIONS,
  INVERSIS_VIA_OPTIONS,
} from './inversis-id-map';

/** Minimum remaining validity required before onboarding submission. */
export const DOCUMENT_MIN_VALIDITY_DAYS = 5;

export const getMinDocumentExpirationDate = (
  referenceDate: Dayjs | Date = dayjs(),
  minValidityDays: number = DOCUMENT_MIN_VALIDITY_DAYS,
): string => {
  return dayjs(referenceDate)
    .startOf('day')
    .add(minValidityDays, 'day')
    .format('YYYY-MM-DD');
};

const meetsDocumentExpirationMinimumValidity = (
  expirationDate: string,
  minValidityDays: number = DOCUMENT_MIN_VALIDITY_DAYS,
): boolean => {
  const parsed = parseDate(expirationDate);

  if (!parsed) {
    return false;
  }

  const expiration = dayjs(parsed).startOf('day');
  const minimumAllowed = dayjs().startOf('day').add(minValidityDays, 'day');

  return !expiration.isBefore(minimumAllowed);
};

export const NIF_NIE_REGEX = /^[0-9]{8}[A-Z]$|^[XYZ][0-9]{7}[A-Z]$/i;

export const validateNifNie = (value: string): boolean => {
  const cleaned = value.trim().toUpperCase();

  return NIF_NIE_REGEX.test(cleaned) && cleaned.length === 9;
};

export const DOCUMENT_TYPES_NIF_NIE_FORMAT = new Set([
  'dni-nif',
  'non-resident-nif',
  'minor-nif',
]);

const addressSchema = z
  .object({
    street_type: z.string(),
    street_name: z.string().min(1, 'Street name is required'),
    street_number: z.string().min(1, 'Street number is required'),
    additional_address_info: z.string().optional(),
    province: z.string(),
    postal_code: z
      .string()
      .min(1, 'Postal code is required')
      .regex(/^\d+$/, 'Postal code must be numeric'),
    city: z.string().min(1, 'City is required'),
    country: z.string().min(1, 'Country is required'),
  })
  .check(({ value, issues }) => {
    const data = value;

    if (!!value.street_type && !INVERSIS_VIA_OPTIONS.has(value.street_type)) {
      issues.push({
        code: 'custom',
        input: data.street_type,
        path: ['street_type'],
        message: 'Invalid street type ID',
      });
    }

    if (!!value.country && !INVERSIS_COUNTRY_OPTIONS.has(value.country)) {
      issues.push({
        code: 'custom',
        input: data.country,
        path: ['country'],
        message: 'Invalid country ID',
      });
    }

    if (value.country === 'spain' && !data.province) {
      issues.push({
        code: 'custom',
        input: data.province,
        path: ['province'],
        message: 'Province is required for the country',
      });
    }

    if (value.country === 'spain' && !data.street_type) {
      issues.push({
        code: 'custom',
        input: data.street_type,
        path: ['street_type'],
        message: 'Street type is required for the country',
      });
    }

    if (!!data.street_type && !INVERSIS_VIA_OPTIONS.has(value.street_type)) {
      issues.push({
        code: 'custom',
        input: data.street_type,
        path: ['street_type'],
        message: 'Invalid street_type ID',
      });
    }

    if (!!data.province && !INVERSIS_PROVINCE_OPTIONS.has(value.province)) {
      issues.push({
        code: 'custom',
        input: data.province,
        path: ['province'],
        message: 'Invalid province ID',
      });
    }
  });

export const personalDataSchema = z
  .object({
    first_name: z.string().min(1, 'First name is required'),
    second_name: z.string().optional(),
    first_surname: z.string().min(1, 'First surname is required'),
    second_surname: z.string().optional(),
    birth_date: z
      .string()
      .min(1, 'Birth date is required')
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid birth date format'),
    marital_status: z.enum(['single', 'married']),
    gender: z.enum(['male', 'female']),
    birth_city: z.string().min(1, 'Birth city is required'),
    nationality: z.string().min(1, 'Nationality is required'),
    document_type: z.string().min(1, 'Document type is required'),
    document_number: z.string().min(1, 'Document number is required'),
    document_expiration_date: z.string().optional(),
    document_permanent_validity: z.boolean(),
    email: z.email('Invalid email address').min(1, 'Email is required'),
    phone_number: z
      .string()
      .min(1, 'Phone number is required')
      .refine(value => {
        try {
          const cleaned = value.trim().replace(/[\s\-()]/g, '');

          return isValidPhoneNumber(cleaned);
        } catch {
          return false;
        }
      }, 'Invalid phone number format'),
    has_different_fiscal_address: z.boolean(),
    postal_address: addressSchema,
    fiscal_address: addressSchema.optional(),
  })
  .check(({ value, issues }) => {
    const data = value;

    if (!!data.nationality && !INVERSIS_COUNTRY_OPTIONS.has(data.nationality)) {
      issues.push({
        code: 'custom',
        input: data.nationality,
        path: ['nationality'],
        message: 'Invalid nationality country ID',
      });
    }

    if (
      !!data.document_type &&
      !INVERSIS_DOCUMENT_TYPE_OPTIONS.has(data.document_type)
    ) {
      issues.push({
        code: 'custom',
        input: data.document_type,
        path: ['document_type'],
        message: 'Invalid document type ID',
      });
    }

    const isInvalidDocumentNumber =
      DOCUMENT_TYPES_NIF_NIE_FORMAT.has(data.document_type) &&
      !validateNifNie(data.document_number);

    if (isInvalidDocumentNumber) {
      issues.push({
        code: 'custom',
        input: data.document_number,
        path: ['document_number'],
        message: 'Invalid NIF/NIE format',
      });
    }

    if (!data.document_permanent_validity && !data.document_expiration_date) {
      issues.push({
        code: 'custom',
        input: data.document_expiration_date,
        path: ['document_expiration_date'],
        message: 'Document expiration date is required',
      });
    }

    if (
      !data.document_permanent_validity &&
      data.document_expiration_date &&
      !meetsDocumentExpirationMinimumValidity(data.document_expiration_date)
    ) {
      issues.push({
        code: 'custom',
        input: data.document_expiration_date,
        path: ['document_expiration_date'],
        message: 'Document must remain valid for at least 5 more days from today',
      });
    }

    if (data.has_different_fiscal_address && !data.fiscal_address) {
      issues.push({
        code: 'custom',
        input: data.fiscal_address,
        path: ['fiscal_address'],
        message: 'Fiscal address is required',
      });
    }
  });

export type PersonalDataFormData = z.infer<typeof personalDataSchema>;
